import { AxiosError, AxiosResponse, isCancel } from 'axios';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  AvailableTaskTypeOptionsResponse,
  AvailableTaskTypesResponse,
  GetAvailableTaskTypesPayload,
} from './task-types-client.type';

const axiosInstance = AxiosInstance();

/**
 * Fetches the distinct normalized task types present within a single
 * entity scope. Used to populate the Process Analysis task-type filter
 * so the dropdown only shows types that actually exist for the currently
 * selected portfolio / project / subproject.
 *
 * @param {GetAvailableTaskTypesPayload} payload - Entity scope to look up
 * @param {AbortSignal} signal - Optional AbortSignal for query cancellation
 * @returns {Promise<AvailableTaskTypesResponse>} Sorted list of normalized type strings
 * @throws {AxiosError} When the API request fails
 */
async function getAvailableTaskTypes(
  payload: GetAvailableTaskTypesPayload,
  signal?: AbortSignal,
): Promise<AvailableTaskTypesResponse> {
  try {
    const url = `${appConfig.VITE_APP_API_URL}/api/task-types/`;
    const params = new URLSearchParams();
    params.append(`${payload.entityType}_id`, payload.entityId);
    const response: AxiosResponse<AvailableTaskTypesResponse> = await axiosInstance.get(url, {
      params,
      signal,
    });
    return response.data;
  } catch (error: unknown) {
    // Re-throw axios cancellation errors unchanged so TanStack Query can
    // recognize them as cancellations rather than legitimate failures.
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    }
    return Promise.reject(new Error('An unknown error occurred while fetching available task types.'));
  }
}

/**
 * Fetches the distinct work-item types within a single entity scope as
 * labeled options (`include_labels=true`). Each option's `value` is the
 * exact stored `raw_type` (the key to feed back as a filter value) and
 * `label` is the verbatim external casing, equal to `value` when no label
 * has been synced yet. Note the semantic difference from
 * `getAvailableTaskTypes`: values here are raw_type keys (e.g. "user story"),
 * not canonical Task.type strings (e.g. "story").
 *
 * @param {GetAvailableTaskTypesPayload} payload - Entity scope to look up
 * @param {AbortSignal} signal - Optional AbortSignal for query cancellation
 * @returns {Promise<AvailableTaskTypeOptionsResponse>} Options sorted by value
 * @throws {AxiosError} When the API request fails
 */
async function getAvailableTaskTypeOptions(
  payload: GetAvailableTaskTypesPayload,
  signal?: AbortSignal,
): Promise<AvailableTaskTypeOptionsResponse> {
  try {
    const url = `${appConfig.VITE_APP_API_URL}/api/task-types/`;
    const params = new URLSearchParams();
    params.append(`${payload.entityType}_id`, payload.entityId);
    params.append('include_labels', 'true');
    const response: AxiosResponse<AvailableTaskTypeOptionsResponse> = await axiosInstance.get(url, {
      params,
      signal,
    });
    return response.data;
  } catch (error: unknown) {
    // Re-throw axios cancellation errors unchanged so TanStack Query can
    // recognize them as cancellations rather than legitimate failures.
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    }
    return Promise.reject(new Error('An unknown error occurred while fetching available task type options.'));
  }
}

export { getAvailableTaskTypeOptions, getAvailableTaskTypes };
